// Opens and closes a shortened text: height, button gap and fade move together; on close the page
// follows the button so it stays put. The closed look comes from CSS on `root:not(.is-open)`.
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const html = document.documentElement;
const ease = (t: number): number => 1 - (1 - t) ** 3;

export default function createFold(root: HTMLElement, text: HTMLElement, button: HTMLElement): () => boolean {
	let open = root.classList.contains('is-open');
	let fade = open ? 1 : 0;
	let frame = 0;

	const clear = (): void => {
		text.style.height = '';
		text.style.overflow = '';
		text.style.maskImage = '';
		text.style.removeProperty('-webkit-mask-image');
		button.style.marginTop = '';
		html.style.overflowAnchor = '';
	};

	return (): boolean => {
		window.cancelAnimationFrame(frame);

		const fromHeight = text.offsetHeight;
		const fromGap = parseFloat(getComputedStyle(button).marginTop);
		const fromFade = fade;
		const pin = button.getBoundingClientRect().top;

		open = !open;
		clear();
		root.classList.toggle('is-open', open);

		const toHeight = text.offsetHeight;
		const toGap = parseFloat(getComputedStyle(button).marginTop);
		const toFade = open ? 1 : 0;
		const follow = (): void => {
			const shift = button.getBoundingClientRect().top - pin;

			if (!open && shift) {
				window.scrollBy({ top: shift, behavior: 'instant' as ScrollBehavior });
			}
		};

		if (reduced.matches || (fromHeight === toHeight && fromGap === toGap)) {
			fade = toFade;
			follow();
			return open;
		}

		const apply = (k: number): void => {
			fade = fromFade + (toFade - fromFade) * k;
			const mask = `linear-gradient(180deg, #000 var(--fold-fade, 60%), rgba(0, 0, 0, ${fade.toFixed(3)}))`;

			text.style.height = `${fromHeight + (toHeight - fromHeight) * k}px`;
			text.style.maskImage = mask;
			text.style.setProperty('-webkit-mask-image', mask);
			button.style.marginTop = `${fromGap + (toGap - fromGap) * k}px`;
			follow();
		};

		const start = performance.now();
		const step = (now: number): void => {
			const t = Math.min(Math.max((now - start) / 600, 0), 1);

			if (t < 1) {
				apply(ease(t));
				frame = window.requestAnimationFrame(step);
				return;
			}

			clear();
			root.classList.toggle('is-open', open);
			fade = toFade;
			follow();
		};

		root.classList.add('is-open');
		text.style.overflow = 'hidden';
		html.style.overflowAnchor = 'none';
		apply(0);
		frame = window.requestAnimationFrame(step);

		return open;
	};
}
